import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Switch,
  FormControlLabel,
  Fab,
  Tooltip,
} from '@mui/material';
import {
  Add,
  VideoCall,
  Edit,
  Delete,
  PlayArrow,
  Stop,
  People,
  Schedule,
  LocationOn,
  MoreVert,
  Refresh,
  Settings,
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  ScreenShare,
  Chat,
  RecordVoiceOver,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import meetingService from '../../services/meetingService';
import clusterService from '../../services/clusterService';
import storeService from '../../services/storeService';
import MeetingRoom from '../../components/meetings/MeetingRoom';
import ProfileAvatar from '../../components/common/ProfileAvatar';
import { 
  Meeting, 
  CreateMeetingRequest,
  UpdateMeetingRequest,
  MeetingPermissionsResponse,
  Cluster,
  Store 
} from '../../types';
import { ROUTES } from '../../constants';

// Validation schema
const meetingSchema = yup.object({
  title: yup.string().required('Meeting title is required'),
  description: yup.string().optional(),
  scheduled_time: yup.string().required('Scheduled time is required'),
  duration_minutes: yup.number().min(15, 'Minimum 15 minutes').max(480, 'Maximum 8 hours').required('Duration is required'),
  meeting_type: yup.string().required('Meeting type is required'),
  cluster_id: yup.string().optional(),
  store_id: yup.string().optional(),
});

const MEETING_TYPES = [
  { value: 'cluster_meeting', label: 'Cluster Meeting' },
  { value: 'store_meeting', label: 'Store Meeting' },
  { value: 'general_meeting', label: 'General Meeting' },
  { value: 'training_meeting', label: 'Training Meeting' },
  { value: 'emergency_meeting', label: 'Emergency Meeting' },
];

const MEETING_STATUS_COLORS = {
  scheduled: 'default',
  ongoing: 'success',
  completed: 'info',
  cancelled: 'error',
};

const MeetingsPage: React.FC = () => {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<Meeting | null>(null);
  const [permissions, setPermissions] = useState<MeetingPermissionsResponse>({
    allow_associates: false,
    allow_external_guests: false,
    allow_screen_sharing: true,
    allow_recording: true,
    allow_chat: true,
    require_approval: false,
  });
  const [permissionsDialog, setPermissionsDialog] = useState(false);
  const [meetingRoomOpen, setMeetingRoomOpen] = useState(false);
  const [currentMeetingId, setCurrentMeetingId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(meetingSchema),
  });

  // Fetch data
  const fetchMeetings = async () => {
    try {
      setLoading(true);
      const response = await meetingService.getMeetings();
      setMeetings(response.meetings);
    } catch (error) {
      console.error('Error fetching meetings:', error);
      setError('Failed to fetch meetings');
    } finally {
      setLoading(false);
    }
  };

  const fetchClusters = async () => {
    try {
      const response = await clusterService.getClusters();
      setClusters(response.clusters);
    } catch (error) {
      console.error('Error fetching clusters:', error);
    }
  };

  const fetchStores = async () => {
    try {
      const response = await storeService.getStores({ limit: 1000 });
      setStores(response.stores || []);
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await meetingService.getMeetingPermissions();
      setPermissions(response);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      // Stagger API calls to prevent rate limiting
      await fetchMeetings();
      await new Promise(resolve => setTimeout(resolve, 200));
      await fetchClusters();
      await new Promise(resolve => setTimeout(resolve, 200));
      await fetchStores();
      await new Promise(resolve => setTimeout(resolve, 200));
      await fetchPermissions();
    };
    loadData();
  }, []);

  const handleCreateMeeting = async (data: any) => {
    try {
      const meetingData: CreateMeetingRequest = {
        ...data,
        // Normalize optional fields and ensure correct datetime format
        cluster_id: data.cluster_id ? data.cluster_id : undefined,
        store_id: data.store_id ? data.store_id : undefined,
        scheduled_time: data.scheduled_time ? new Date(data.scheduled_time).toISOString() : undefined,
        permissions,
      };
      await meetingService.createMeeting(meetingData);
      setOpenDialog(false);
      reset();
      fetchMeetings();
    } catch (error: any) {
      console.error('Error creating meeting:', error);
      setError(error.response?.data?.message || 'Failed to create meeting');
    }
  };

  const handleEditMeeting = (meeting: Meeting) => {
    setEditingMeeting(meeting);
    setValue('title', meeting.title);
    setValue('description', meeting.description || '');
    setValue('scheduled_time', meeting.scheduled_time);
    setValue('duration_minutes', meeting.duration_minutes);
    setValue('meeting_type', meeting.meeting_type);
    setValue('cluster_id', meeting.cluster_id || '');
    setValue('store_id', meeting.store_id || '');
    setPermissions(meeting.permissions);
    setOpenDialog(true);
  };

  const handleUpdateMeeting = async (data: any) => {
    if (!editingMeeting) return;
    try {
      const meetingData: UpdateMeetingRequest = {
        ...data,
        cluster_id: data.cluster_id ? data.cluster_id : undefined,
        store_id: data.store_id ? data.store_id : undefined,
        scheduled_time: data.scheduled_time ? new Date(data.scheduled_time).toISOString() : undefined,
        permissions,
      };
      await meetingService.updateMeeting(editingMeeting.id, meetingData);
      setOpenDialog(false);
      setEditingMeeting(null);
      reset();
      fetchMeetings();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update meeting');
    }
  };

  const handleDeleteMeeting = async (meetingId: string) => {
    if (window.confirm('Are you sure you want to delete this meeting?')) {
      try {
        await meetingService.deleteMeeting(meetingId);
        fetchMeetings();
      } catch (error: any) {
        setError(error.response?.data?.message || 'Failed to delete meeting');
      }
    }
  };

  const handleStartMeeting = async (meetingId: string) => {
    try {
      await meetingService.startMeeting(meetingId);
      fetchMeetings();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to start meeting');
    }
  };

  const handleEndMeeting = async (meetingId: string) => {
    try {
      await meetingService.endMeeting(meetingId);
      fetchMeetings();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to end meeting');
    }
  };

  const handleJoinMeeting = async (meetingId: string) => {
    try {
      setCurrentMeetingId(meetingId);
      setMeetingRoomOpen(true);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to join meeting');
    }
  };

  const handleLeaveMeetingRoom = () => {
    setMeetingRoomOpen(false);
    setCurrentMeetingId(null);
  };

  const handleUpdatePermissions = async () => {
    try {
      await meetingService.updateMeetingPermissions(permissions);
      setPermissionsDialog(false);
      fetchPermissions();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update permissions');
    }
  };

  const formatDateTime = (dateTime: string | undefined) => {
    if (!dateTime) return 'Not scheduled';
    return new Date(dateTime).toLocaleString();
  };

  const formatDuration = (minutes: number | undefined) => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Show meeting room if user joined a meeting
  if (meetingRoomOpen && currentMeetingId) {
    return (
      <MeetingRoom
        meetingId={currentMeetingId}
        onLeave={handleLeaveMeetingRoom}
      />
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ color: '#081122', fontWeight: 600, mb: 1 }}>
            Meetings
          </Typography>
          <Typography variant="body1" sx={{ color: '#666666' }}>
            Schedule and manage video meetings for your team
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Settings />}
            onClick={() => setPermissionsDialog(true)}
          >
            Permissions
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenDialog(true)}
            sx={{
              background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
              },
              boxShadow: '0 4px 12px rgba(8, 17, 34, 0.3)',
            }}
          >
            Schedule Meeting
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Meetings Grid */}
      <Grid container spacing={3}>
        {(meetings || []).map((meeting) => (
          <Grid item xs={12} md={6} lg={4} key={meeting?.id || Math.random()}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease',
              }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#081122' }}>
                    {meeting?.title || 'Untitled Meeting'}
                  </Typography>
                  <Chip
                    label={(meeting?.status || 'unknown').toUpperCase()}
                    color={MEETING_STATUS_COLORS[meeting?.status || 'unknown'] as any}
                    size="small"
                  />
                </Box>

                <Typography variant="body2" sx={{ color: '#666666', mb: 2 }}>
                  {meeting?.description || 'No description'}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Schedule sx={{ fontSize: 16, color: '#6c757d', mr: 1 }} />
                  <Typography variant="body2" sx={{ color: '#333333' }}>
                    {formatDateTime(meeting?.scheduled_time)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <VideoCall sx={{ fontSize: 16, color: '#6c757d', mr: 1 }} />
                  <Typography variant="body2" sx={{ color: '#333333' }}>
                    {formatDuration(meeting?.duration_minutes)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <People sx={{ fontSize: 16, color: '#6c757d', mr: 1 }} />
                  <Typography variant="body2" sx={{ color: '#333333' }}>
                    {meeting?.participants?.filter(p => p.user).length || 0} participants
                  </Typography>
                </Box>

                {/* Participants List */}
                {meeting?.participants && meeting.participants.filter(p => p.user).length > 0 && (
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <Typography variant="caption" sx={{ color: '#666666', mb: 1, display: 'block' }}>
                      Participants:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {meeting.participants.slice(0, 3).map((participant, index) => {
                        // Skip participants without user data
                        if (!participant.user) return null;
                        
                        const firstName = participant.user.first_name || '';
                        const lastName = participant.user.last_name || '';
                        const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`;
                        
                        return (
                          <Box key={participant.user_id || index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ProfileAvatar
                              imageUrl={participant.user.profile_image_url}
                              firstName={participant.user.first_name}
                              lastName={participant.user.last_name}
                              sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: '#1976d2' }}
                            />
                            <Typography variant="caption" sx={{ color: '#333333' }}>
                              {firstName} {lastName}
                            </Typography>
                          </Box>
                        );
                      }).filter(Boolean)}
                      {meeting.participants.filter(p => p.user).length > 3 && (
                        <Typography variant="caption" sx={{ color: '#666666', alignSelf: 'center' }}>
                          +{meeting.participants.filter(p => p.user).length - 3} more
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                {meeting?.cluster && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocationOn sx={{ fontSize: 16, color: '#6c757d', mr: 1 }} />
                    <Typography variant="body2" sx={{ color: '#333333' }}>
                      {meeting?.cluster?.name || 'Unknown Cluster'}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mt: 2 }}>
                  <Chip
                    label={MEETING_TYPES.find(t => t.value === meeting?.meeting_type)?.label || 'Unknown Type'}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              </CardContent>

              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Box>
                  {meeting?.status === 'scheduled' && (
                    <Button
                      size="small"
                      startIcon={<PlayArrow />}
                      onClick={() => handleStartMeeting(meeting?.id)}
                    >
                      Start
                    </Button>
                  )}
                  {meeting?.status === 'ongoing' && (
                    <>
                      <Button
                        size="small"
                        startIcon={<VideoCall />}
                        onClick={() => handleJoinMeeting(meeting?.id)}
                        color="primary"
                      >
                        Join
                      </Button>
                      <Button
                        size="small"
                        startIcon={<Stop />}
                        onClick={() => handleEndMeeting(meeting?.id)}
                        color="error"
                      >
                        End
                      </Button>
                    </>
                  )}
                </Box>
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => handleEditMeeting(meeting)}
                    title="Edit Meeting"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteMeeting(meeting?.id)}
                    title="Delete Meeting"
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Meeting Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingMeeting ? 'Edit Meeting' : 'Schedule New Meeting'}</DialogTitle>
        <form onSubmit={handleSubmit((data) => {
          if (editingMeeting) {
            handleUpdateMeeting(data);
          } else {
            handleCreateMeeting(data);
          }
        })}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  {...register('title')}
                  label="Meeting Title"
                  fullWidth
                  error={!!errors.title}
                  helperText={errors.title?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...register('description')}
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  {...register('scheduled_time')}
                  label="Scheduled Time"
                  type="datetime-local"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.scheduled_time}
                  helperText={errors.scheduled_time?.message}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  {...register('duration_minutes')}
                  label="Duration (minutes)"
                  type="number"
                  fullWidth
                  error={!!errors.duration_minutes}
                  helperText={errors.duration_minutes?.message}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth error={!!errors.meeting_type}>
                  <InputLabel>Meeting Type</InputLabel>
                  <Select
                    {...register('meeting_type')}
                    label="Meeting Type"
                    value={watch('meeting_type') || ''}
                    onChange={(e) => setValue('meeting_type', e.target.value)}
                  >
                    {MEETING_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.meeting_type && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.meeting_type.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth error={!!errors.cluster_id}>
                  <InputLabel>Cluster (Optional)</InputLabel>
                  <Select
                    {...register('cluster_id')}
                    label="Cluster (Optional)"
                    value={watch('cluster_id') || ''}
                    onChange={(e) => setValue('cluster_id', e.target.value)}
                  >
                    {clusters.map((cluster) => (
                      <MenuItem key={cluster.id} value={cluster.id}>
                        {cluster.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingMeeting ? 'Update Meeting' : 'Schedule Meeting'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={permissionsDialog} onClose={() => setPermissionsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Meeting Permissions</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={permissions.allow_associates}
                  onChange={(e) => setPermissions(prev => ({ ...prev, allow_associates: e.target.checked }))}
                />
              }
              label="Allow Associates to Join Meetings"
            />
            <Typography variant="caption" sx={{ color: '#666666', ml: 4 }}>
              Associates can join meetings if enabled
            </Typography>
          </Box>
          <Box sx={{ pt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={permissions.allow_external_guests}
                  onChange={(e) => setPermissions(prev => ({ ...prev, allow_external_guests: e.target.checked }))}
                />
              }
              label="Allow External Guests"
            />
            <Typography variant="caption" sx={{ color: '#666666', ml: 4 }}>
              External users can join meetings
            </Typography>
          </Box>
          <Box sx={{ pt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={permissions.allow_screen_sharing}
                  onChange={(e) => setPermissions(prev => ({ ...prev, allow_screen_sharing: e.target.checked }))}
                />
              }
              label="Allow Screen Sharing"
            />
          </Box>
          <Box sx={{ pt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={permissions.allow_recording}
                  onChange={(e) => setPermissions(prev => ({ ...prev, allow_recording: e.target.checked }))}
                />
              }
              label="Allow Recording"
            />
          </Box>
          <Box sx={{ pt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={permissions.allow_chat}
                  onChange={(e) => setPermissions(prev => ({ ...prev, allow_chat: e.target.checked }))}
                />
              }
              label="Allow Chat"
            />
          </Box>
          <Box sx={{ pt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={permissions.require_approval}
                  onChange={(e) => setPermissions(prev => ({ ...prev, require_approval: e.target.checked }))}
                />
              }
              label="Require Approval to Join"
            />
            <Typography variant="caption" sx={{ color: '#666666', ml: 4 }}>
              Participants need approval before joining
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermissionsDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdatePermissions} variant="contained">
            Save Permissions
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MeetingsPage;
